import React, { useEffect, useMemo, useState } from "react";
import { IconPlugConnected, IconCheck } from "@tabler/icons-react";
import EditorToolbarIcon from "./EditorToolbarIcon";
import { CHROME_TOOLBAR_ICON_GLYPH } from "../utils/editorChromeTheme";
import {
  ActionIcon,
  Tooltip,
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Box,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  editorModalShell,
  editorModalStrongTitleStyles,
  editorModalPrimaryButtonStyles,
  editorModalBorder,
} from "../styleFile";
import { XANO_MCP_SSE, API_MCP_TOKEN, API_MCP_TOKEN_STATUS } from "../config/xano";

const SNIPPET_TABS = [
  { id: "claude", label: "Claude" },
  { id: "cursor", label: "Cursor" },
  { id: "antigravity", label: "Antigravity" },
  { id: "windsurf", label: "Windsurf" },
];

function buildClaudeSnippet(sseUrl, token) {
  return `{
  "mcpServers": {
    "matchboxxr-user-read": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-remote",
        "${sseUrl}",
        "--header",
        "Authorization: Bearer ${token}"
      ]
    }
  }
}`;
}

function buildCursorSnippet(sseUrl, token) {
  return `{
  "mcpServers": {
    "matchboxxr-user-read": {
      "url": "${sseUrl}",
      "headers": {
        "Authorization": "Bearer ${token}"
      }
    }
  }
}`;
}

function buildAntigravitySnippet(sseUrl, token) {
  return `{
  "mcpServers": {
    "matchboxxr-user-read": {
      "serverUrl": "${sseUrl}",
      "headers": {
        "Authorization": "Bearer ${token}"
      }
    }
  }
}`;
}

function buildWindsurfSnippet(sseUrl, token) {
  return ` "matchboxxr-user-read": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-remote",
        ${sseUrl},
        "--header",
        "Authorization: Bearer ${token}"
      ]
    }`;
}

function buildSnippet(tabId, sseUrl, token) {
  switch (tabId) {
    case "claude": return buildClaudeSnippet(sseUrl, token);
    case "cursor": return buildCursorSnippet(sseUrl, token);
    case "antigravity": return buildAntigravitySnippet(sseUrl, token);
    case "windsurf": return buildWindsurfSnippet(sseUrl, token);
    default: return buildAntigravitySnippet(sseUrl, token);
  }
}

function getConfigPath(tabId) {
  switch (tabId) {
    case "claude": return "~/Library/Application Support/Claude/claude_desktop_config.json";
    case "cursor": return ".cursor/mcp.json (project) or ~/.cursor/mcp.json (global)";
    case "antigravity": return "~/.gemini/antigravity/mcp_config.json";
    case "windsurf": return "~/.codeium/windsurf/mcp_config.json";
    default: return "";
  }
}

function maskUrlForDisplay(url) {
  try {
    const u = new URL(url);
    const origin = `${u.protocol}//${u.host}`;
    const path = u.pathname;
    const query = u.search;
    const tail = `${path}${query}`;
    if (tail.length <= 28) return `${origin}${tail}`;
    return `${origin}${tail.slice(0, 18)}********${tail.slice(-10)}`;
  } catch {
    if (!url) return "";
    if (url.length <= 28) return url;
    return `${url.slice(0, 18)}********${url.slice(-10)}`;
  }
}

const mcpSubtleCardStyle = {
  border: `1px solid ${editorModalBorder}`,
  borderRadius: 4,
  background: "var(--mbx-modal-muted-fill, #f9fafb)",
  padding: 12,
};

const mcpCodeShellStyle = {
  border: "1px solid #0f172a",
  borderRadius: 4,
  overflow: "hidden",
  background: "#0b1220",
};

const mcpCodeHeaderStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.04)",
};

const mcpCodeBlockStyle = {
  margin: 0,
  borderRadius: 0,
  padding: "12px 14px",
  fontSize: 12,
  lineHeight: 1.65,
  background: "transparent",
  color: "#e5e7eb",
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace",
};

// Renders each JSON line separately so paddingLeft+textIndent gives a perfect
// hanging indent on wrap — continuation lines align with the line's own indentation.
function CodeLine({ line }) {
  const spaces = (line.match(/^ */) || [""])[0].length;
  return (
    <div
      style={{
        paddingLeft: `${spaces}ch`,
        textIndent: `-${spaces}ch`,
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
      }}
    >
      {line}
    </div>
  );
}

function formatExpiresIn(expiresAt) {
  if (expiresAt == null || expiresAt === "") return "n/a";

  let ms = Number(expiresAt);
  if (Number.isFinite(ms)) {
    // Normalize seconds to milliseconds when needed.
    if (ms > 0 && ms < 1e12) ms *= 1000;
  } else {
    ms = new Date(expiresAt).getTime();
  }

  if (!Number.isFinite(ms) || ms <= 0) return "n/a";
  const dt = new Date(ms);
  if (Number.isNaN(dt.getTime())) return "n/a";
  return dt.toLocaleString();
}

export default function MCPConnectButton({ iconColour, inGroup = false }) {
  const primaryTextColor = "var(--mbx-modal-primary-text, #111827)";
  const [opened, { open, close }] = useDisclosure(false);
  const [copied, setCopied] = useState("");
  const [activeTab, setActiveTab] = useState("claude");

  // Session auth token used by Matchboxxr to manage MCP tokens.
  const [sessionToken, setSessionToken] = useState("");

  // MCP token secret (one-time): server returns raw_token once, we keep it
  // only in memory until the user copies it successfully.
  const [mcpTokenRaw, setMcpTokenRaw] = useState("");
  const [mcpTokenMeta, setMcpTokenMeta] = useState(null);
  const [mcpTokenExists, setMcpTokenExists] = useState(false);
  const [tokenStatusLoading, setTokenStatusLoading] = useState(false);
  const [rotateLoading, setRotateLoading] = useState(false);
  const [tokenActionMessage, setTokenActionMessage] = useState("");
  const tokenExpiresInLabel = useMemo(
    () => formatExpiresIn(mcpTokenMeta?.expires_at),
    [mcpTokenMeta]
  );

  const maskedSseUrl = useMemo(() => maskUrlForDisplay(XANO_MCP_SSE), []);

  // Snippet display is always masked.
  const displaySnippet = useMemo(
    () => buildSnippet(activeTab, maskedSseUrl, "********"),
    [activeTab, maskedSseUrl]
  );
  // Snippet copy uses raw_token and is only enabled while the raw secret is present.
  const copySnippet = useMemo(() => {
    if (!mcpTokenRaw) return "";
    return buildSnippet(activeTab, XANO_MCP_SSE, mcpTokenRaw);
  }, [activeTab, mcpTokenRaw]);

  const consumeSecretCopy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
      // Hide the secret after copy, but keep it in memory for additional copies.
      setTokenActionMessage("");
    } catch {
      setCopied("");
      setTokenActionMessage("Copy failed. Please try again.");
    }
  };

  const refreshMcpTokenStatus = async (bearerToken) => {
    setTokenStatusLoading(true);
    setTokenActionMessage("");
    try {
      if (!bearerToken) {
        setMcpTokenExists(false);
        setMcpTokenMeta(null);
        return;
      }

      const res = await fetch(API_MCP_TOKEN_STATUS, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          Accept: "application/json",
        },
      });
      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setMcpTokenExists(false);
        setMcpTokenMeta(null);
        setTokenActionMessage("Could not load MCP token status.");
        return;
      }

      if (data.exists && data.token) {
        setMcpTokenExists(true);
        setMcpTokenMeta(data.token);
      } else {
        setMcpTokenExists(false);
        setMcpTokenMeta(null);
      }
    } catch {
      setMcpTokenExists(false);
      setMcpTokenMeta(null);
      setTokenActionMessage("Could not load MCP token status.");
    } finally {
      setTokenStatusLoading(false);
    }
  };

  useEffect(() => {
    if (opened) {
      const userBearer = localStorage.getItem("user") || "";
      setSessionToken(userBearer);

      // Always clear raw secret when opening; we only fetch preview/metadata.
      setMcpTokenRaw("");
      setTokenActionMessage("");

      refreshMcpTokenStatus(userBearer);
    } else {
      // If modal closes before copy, still clear the raw secret.
      setMcpTokenRaw("");
      setTokenActionMessage("");
    }
  }, [opened]);

  const handleGenerateRotateToken = async () => {
    const bearer = sessionToken || localStorage.getItem("user") || "";
    if (!bearer) {
      setTokenActionMessage("Please log in and try again.");
      return;
    }
    setRotateLoading(true);
    setTokenActionMessage("");

    try {
      const res = await fetch(API_MCP_TOKEN, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${bearer}`,
          "content-type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: "Matchboxxr User MCP",
          scope: ["export:read"],
          expires_in_days: 90,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        setTokenActionMessage(
          data?.error?.message || `Token generation failed (HTTP ${res.status}).`
        );
        return;
      }

      const rawToken = data.raw_token || data.rawToken || "";

      if (!rawToken) {
        setTokenActionMessage("Token generation succeeded but no raw token was returned.");
        return;
      }

      setMcpTokenRaw(rawToken);
      setMcpTokenExists(true);

      // Best-effort metadata capture for the token panel.
      setMcpTokenMeta({
        token_id: data.token_id,
        token_preview: data.token_preview || data.tokenPreview || "",
        scope: data.scope,
        status: data.status,
        expires_at: data.expires_at,
      });

      setTokenActionMessage("");
    } catch {
      setTokenActionMessage("Token generation failed. Please try again.");
    } finally {
      setRotateLoading(false);
    }
  };

  return (
    <>
      <EditorToolbarIcon label="Connect AI via MCP" onClick={open} iconColour={iconColour} inGroup={inGroup}>
        <IconPlugConnected size={CHROME_TOOLBAR_ICON_GLYPH} />
      </EditorToolbarIcon>

      <Modal
        opened={opened}
        onClose={close}
        title="Connect AI Agent – MCP (BETA)"
        withCloseButton
        size={620}
        zIndex={300}
        radius={editorModalShell.radius}
        centered={editorModalShell.centered}
        overlayProps={editorModalShell.overlayProps}
        styles={editorModalStrongTitleStyles}
      >
        <Stack gap="md">
          <Box
            px={20}
            py={20}
            style={{
              width: "100%",
              borderBottom: `1px solid ${editorModalBorder}`,
              background: "var(--mbx-modal-muted-fill, #f9fafb)",
            }}
          >
            <Text size="sm" c={primaryTextColor} ta="center" style={{ lineHeight: 1.5 }}>
              Connect your AI agent to Matchboxxr via Model Context Protocol (MCP) using a backend-generated,
              user-scoped MCP token. Provides read-only access to your project data. This MCP connection is currently in beta.
            </Text>
          </Box>

          <Stack gap="md" px={20} pb={20}>
            <Box style={mcpCodeShellStyle}>
              {/* Tab bar */}
              <Box
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 2,
                  padding: "8px 10px 0",
                  background: "rgba(255,255,255,0.04)",
                  borderBottom: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                {SNIPPET_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      background: activeTab === tab.id ? "#0b1220" : "transparent",
                      border: "none",
                      borderRadius: "4px 4px 0 0",
                      color: activeTab === tab.id ? "#e5e7eb" : "#6b7280",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: activeTab === tab.id ? 700 : 400,
                      padding: "5px 12px 6px",
                      transition: "color 0.15s",
                      letterSpacing: "0.01em",
                      fontFamily: "ui-sans-serif, system-ui, sans-serif",
                      outline: "none",
                      marginBottom: activeTab === tab.id ? -1 : 0,
                      borderBottom: activeTab === tab.id ? "1px solid #0b1220" : "none",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </Box>
              {/* Config path hint */}
              <Box style={{ padding: "6px 14px 0", background: "#0b1220" }}>
                <Text size="10px" c="gray.6" style={{ fontFamily: "ui-monospace, monospace" }}>
                  {getConfigPath(activeTab)}
                </Text>
              </Box>
              <pre style={mcpCodeBlockStyle}>
                {displaySnippet.split("\n").map((line, i) => (
                  <CodeLine key={i} line={line} />
                ))}
              </pre>
              <Box
                style={{
                  padding: "10px 12px",
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.02)",
                }}
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Text size="11px" c="gray.3">
                    Status: {tokenStatusLoading ? "Loading…" : mcpTokenExists ? "Active" : "None"}
                    {`  •  Expires: ${tokenExpiresInLabel}`}
                  </Text>
                  <Group gap="xs" wrap="nowrap">
                    <Button
                      radius="md"
                      styles={editorModalPrimaryButtonStyles}
                      onClick={handleGenerateRotateToken}
                      loading={rotateLoading}
                      size="xs"
                    >
                      Generate token
                    </Button>
                    <Button
                      radius="md"
                      variant="default"
                      onClick={() =>
                        copySnippet &&
                        consumeSecretCopy(copySnippet, "snippet-desktop")
                      }
                      disabled={!mcpTokenRaw || !copySnippet}
                      leftSection={
                        copied === "snippet-desktop" ? <IconCheck size={14} /> : null
                      }
                      size="xs"
                    >
                      {copied === "snippet-desktop"
                        ? "Copied"
                        : mcpTokenRaw
                          ? "Copy snippet"
                          : "Copy snippet"}
                    </Button>
                  </Group>
                </Group>
                {mcpTokenRaw ? (
                  <Text size="10px" c="gray.5" mt={6}>
                    Keep this token private. If leaked, rotate or revoke it.
                  </Text>
                ) : null}
              </Box>
            </Box>
            <Box style={mcpSubtleCardStyle}>
              <Text size="xs" c={primaryTextColor} style={{ lineHeight: 1.55 }}>
                Setup steps:
                <br />1. Select your AI tool above and generate a token.
                <br />2. Click <strong>Copy snippet</strong> — the full token is embedded.
                <br />3. Open your AI app MCP settings and paste into the config file shown.
                <br />4. Save and reconnect / restart your AI app.
                <br />5. The token is masked in the preview for safety.
              </Text>
            </Box>

            {tokenActionMessage ? (
              <Text size="xs" c={primaryTextColor}>
                {tokenActionMessage}
              </Text>
            ) : null}
          </Stack>
        </Stack>
      </Modal>
    </>
  );
}
